/**
 * Наряд-допуск (КЧ-05, КЧ-20): изоляции LOTO, одновременные работы (SIMOPS) и продление. Способы изоляции и виды
 * работ, которым нужны изоляции, — из настроек нарядов компании; все проверки повторяет сервер.
 */
import {
  ISOLATION_STATES,
  type IsolationState,
  PERMIT_KINDS,
  PERMIT_STATUSES,
  type PermitDetail,
} from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, type Tone, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { localToIso } from './safetyShared';

const TONE: Record<IsolationState, Tone> = {
  planned: 'neutral',
  installed: 'warning',
  verified: 'success',
  removed: 'neutral',
};

const failed = (e: unknown) => toast(e instanceof Error ? e.message : 'Не получилось', 'danger');

export function PermitIsolationsPanel({ d, onDone }: { d: PermitDetail; onDone: (d: PermitDetail) => void }) {
  const [point, setPoint] = useState('');
  const [method, setMethod] = useState(d.isolationMethods[0] ?? '');
  const [tag, setTag] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api<PermitDetail>(`/quality/permits/${d.id}/isolations`, {
        body: { point: point.trim(), method, tag: tag.trim() || null },
      }),
    onSuccess: (r) => {
      onDone(r);
      setPoint('');
      setTag('');
    },
  });
  const mark = useMutation({
    mutationFn: (a: { id: string; action: 'install' | 'verify' | 'remove' }) =>
      api<PermitDetail>(`/quality/permits/${d.id}/isolations/${a.id}/mark`, { body: { action: a.action } }),
    onSuccess: (r, a) => {
      onDone(r);
      toast(
        a.action === 'install'
          ? 'Установка отмечена'
          : a.action === 'verify'
            ? 'Проверка отмечена'
            : 'Изоляция снята',
      );
    },
    onError: failed,
  });
  if (!d.lotoRequired && !d.isolations.length && !d.can.isolations) return null;
  const left = d.isolations.filter((i) => i.state === 'installed' || i.state === 'verified').length;
  return (
    <Panel
      title={`Изоляции LOTO (${d.isolations.length})`}
      meta={
        d.lotoRequired
          ? 'для этого вида работ обязательны; проверяет другой человек, не тот, кто установил'
          : 'проверяет другой человек, не тот, кто установил'
      }
      className="mb-6"
    >
      {d.isolations.length ? (
        <ul className="mb-4 flex flex-col gap-2">
          {d.isolations.map((i) => (
            <li
              key={i.id}
              className="flex flex-col gap-2 rounded-md border border-line p-3 text-[14px] text-ink"
            >
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-3">
                <span className="min-w-0 flex-1 font-semibold [overflow-wrap:anywhere]">{i.point}</span>
                <Badge tone={TONE[i.state]}>{ISOLATION_STATES[i.state]}</Badge>
              </div>
              <span className="text-ink-2 [overflow-wrap:anywhere]">
                {i.method}
                {i.tag ? ` · ${i.tag}` : ''}
              </span>
              <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                {[
                  i.installedAt ? `установил ${i.installedByName ?? ''} ${dateTime(i.installedAt)}` : null,
                  i.verifiedAt ? `проверил ${i.verifiedByName ?? ''} ${dateTime(i.verifiedAt)}` : null,
                  i.removedAt ? `снял ${i.removedByName ?? ''} ${dateTime(i.removedAt)}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {d.can.isolationMark ? (
                <div className="flex flex-wrap gap-2">
                  {i.state === 'planned' && ['draft', 'signing', 'signed'].includes(d.status) ? (
                    <Button
                      variant="secondary"
                      className="h-11 md:h-10"
                      loading={mark.isPending}
                      onClick={() => mark.mutate({ id: i.id, action: 'install' })}
                    >
                      Установлено
                    </Button>
                  ) : null}
                  {i.state === 'installed' &&
                  i.canVerify &&
                  ['draft', 'signing', 'signed'].includes(d.status) ? (
                    <Button
                      variant="secondary"
                      className="h-11 md:h-10"
                      loading={mark.isPending}
                      onClick={() => mark.mutate({ id: i.id, action: 'verify' })}
                    >
                      Проверено мной
                    </Button>
                  ) : null}
                  {(i.state === 'installed' || i.state === 'verified') &&
                  ['done', 'cancelled'].includes(d.status) ? (
                    <Button
                      variant="secondary"
                      className="h-11 md:h-10"
                      loading={mark.isPending}
                      disabled={i.removeBlockers.length > 0}
                      onClick={() => mark.mutate({ id: i.id, action: 'remove' })}
                    >
                      Снять изоляцию
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {i.removeBlockers.length && ['done', 'cancelled'].includes(d.status) ? (
                <p className="text-[13px] text-danger">{i.removeBlockers.join('; ')}</p>
              ) : null}
              {d.can.isolations && i.state === 'planned' ? (
                <button
                  type="button"
                  className="inline-flex min-h-11 items-center self-start text-[13px] text-danger hover:underline md:min-h-0"
                  onClick={() =>
                    api<PermitDetail>(`/quality/permits/${d.id}/isolations/${i.id}`, { method: 'DELETE' })
                      .then(onDone)
                      .catch(failed)
                  }
                >
                  Убрать точку
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[14px] text-ink-2">Точек изоляции нет.</p>
      )}
      {left && d.status === 'done' ? (
        <p className="mb-4 text-[14px] text-ink">
          Работы завершены: снимите изоляции ({left}), затем закройте наряд.
        </p>
      ) : null}
      {d.can.isolations ? (
        <form
          className="flex flex-col gap-3 border-t border-line pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field
              label="Точка изоляции"
              hint="например: задвижка № 3 на входе резервуара"
              htmlFor="iso-point"
            >
              <Input
                id="iso-point"
                className="h-11 md:h-10"
                maxLength={200}
                value={point}
                onChange={(e) => setPoint(e.target.value)}
              />
            </Field>
            <Field label="Способ" htmlFor="iso-method">
              <Select
                id="iso-method"
                className="h-11 md:h-10"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                {d.isolationMethods.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="№ замка, бирки, заглушки" htmlFor="iso-tag">
              <Input
                id="iso-tag"
                className="h-11 md:h-10"
                maxLength={60}
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              />
            </Field>
          </div>
          {add.isError ? <ErrorBox error={add.error} /> : null}
          <div>
            <Button
              type="submit"
              variant="secondary"
              size="lg"
              loading={add.isPending}
              disabled={point.trim().length < 2}
            >
              Добавить точку
            </Button>
          </div>
        </form>
      ) : null}
    </Panel>
  );
}

/** Одновременные работы: другие наряды проекта в то же время; обоснование вносится, пока наряд готовится */
export function PermitSimopsPanel({ d, onDone }: { d: PermitDetail; onDone: (d: PermitDetail) => void }) {
  const [note, setNote] = useState(d.simopsNote ?? '');
  const save = useMutation({
    mutationFn: () =>
      api<PermitDetail>(`/quality/permits/${d.id}`, {
        method: 'PUT',
        body: {
          projectId: d.projectId,
          objectId: d.objectId,
          activityId: d.activityId,
          contractorId: d.contractorId,
          kind: d.kind,
          title: d.title,
          place: d.place,
          measures: d.measures,
          responsibleId: d.responsibleId,
          approverId: d.approverId,
          issuerId: d.issuerId,
          validFrom: d.validFrom,
          validUntil: d.validUntil,
          simopsNote: note.trim() || null,
        },
      }),
    onSuccess: (r) => {
      onDone(r);
      toast('Обоснование сохранено');
    },
  });
  if (!d.simops.length && !d.simopsNote) return null;
  const bad = d.simops.some((x) => x.level === 'bad');
  return (
    <Panel
      title="Одновременные работы"
      meta="другие наряды проекта в то же время"
      actions={bad ? <Badge tone="danger">Нужно обоснование</Badge> : null}
      className="mb-6"
    >
      {d.simops.length ? (
        <ul className="mb-4 flex flex-col gap-2">
          {d.simops.map((x) => (
            <li
              key={x.permitId}
              className="flex flex-col gap-1 rounded-md border border-line p-3 text-[14px] text-ink"
            >
              <Link
                to="/qhse/permits/$permitId"
                params={{ permitId: x.permitId }}
                className="inline-flex min-h-11 items-center underline md:min-h-0 [overflow-wrap:anywhere]"
              >
                {x.number}: {x.title}
              </Link>
              <span className="text-ink-2">
                {PERMIT_KINDS[x.kind].title} · {PERMIT_STATUSES[x.status]} · {x.reason}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {d.can.edit ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Обоснование одновременных работ и дополнительные меры" htmlFor="simops-note">
            <Textarea
              id="simops-note"
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          {save.isError ? <ErrorBox error={save.error} /> : null}
          <div>
            <Button type="submit" variant="secondary" size="lg" loading={save.isPending}>
              Сохранить обоснование
            </Button>
          </div>
        </form>
      ) : d.simopsNote ? (
        <p className="text-[14px] text-ink [overflow-wrap:anywhere]">Обоснование: {d.simopsNote}</p>
      ) : null}
    </Panel>
  );
}

/** Продление наряда: подписывает выдавший; новый срок — не больше наибольшего по настройкам от начала наряда */
export function ExtendDialog({
  d,
  onDone,
  onClose,
}: {
  d: PermitDetail;
  onDone: (d: PermitDetail) => void;
  onClose: () => void;
}) {
  const p = (n: number) => String(n).padStart(2, '0');
  const limit = Date.parse(d.validFrom) + d.maxDays * 86_400_000;
  const local = (t: number) => {
    const x = new Date(t);
    return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}T${p(x.getHours())}:${p(x.getMinutes())}`;
  };
  const plus = new Date(Math.min(Date.parse(d.validUntil) + 86_400_000, limit));
  const [until, setUntil] = useState(local(plus.getTime()));
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api<PermitDetail>(`/quality/permits/${d.id}/extend`, {
        body: { validUntil: localToIso(until), reason: reason.trim(), simopsNote: note.trim() || null },
      }),
    onSuccess: (r) => {
      onDone(r);
      toast('Наряд продлён');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Продлить ${d.number}`}
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Не надо
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={save.isPending}
            disabled={!until || reason.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Продлить и подписать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[14px] text-ink-2">
          Сейчас действует до {dateTime(d.validUntil)}. Наряд действует не больше {d.maxDays} дн. от начала (
          {dateTime(d.validFrom)}); допуски бригады должны действовать и на новый срок.
        </p>
        <Field label="Новое окончание" htmlFor="extend-until">
          <Input
            id="extend-until"
            type="datetime-local"
            className="h-11 md:h-10"
            value={until}
            max={local(limit)}
            onChange={(e) => setUntil(e.target.value)}
          />
        </Field>
        <Field label="Причина продления" htmlFor="extend-reason">
          <Textarea
            id="extend-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Field
          label="Обоснование одновременных работ"
          hint="нужно, если в новый срок рядом окажутся огневые или газоопасные работы"
          htmlFor="extend-simops"
        >
          <Textarea
            id="extend-simops"
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        {save.isError ? <ErrorBox error={save.error} /> : null}
      </div>
    </Dialog>
  );
}
